import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { PencilSimpleLine } from "@phosphor-icons/react";
import React, { useState } from "react";
import { iconSize } from "../../const/sizes";
import { AdminUser } from "../../const/types";
import { useFormik } from "formik";
import * as yup from "yup";
import cr from "../../lib/request";
import useTrigger from "../../globalState/useTrigger";
import RequiredForm from "../RequiredForm";
import PasswordInput from "../PasswordInput";
import adminRoles from "../../const/adminRoles";
import FileUploader from "../FileUploader";

type Props = {
  data: AdminUser;
};

export default function UpdateAccountModal({ data }: Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const { trigger, setTrigger } = useTrigger();

  const [fotoProfilPreview, setFotoProfilPreview] = useState<string | null>(
    null
  );

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      name: data.name,
      username: data.username,
      password: "",
      adminAccount: data.roles.adminAccount ? true : false,
      adminCmsLandingpage: data.roles.adminCmsLandingpage ? true : false,
      adminBlog: data.roles.adminBlog ? true : false,
      adminCourse: data.roles.adminCourse ? true : false,
      email: data.email,
      phone: data.phone,
      image: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      name: yup.string().required("Nama harus diisi"),
      username: yup.string().required("Username harus diisi"),
      image: yup
        .array()
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC yang diperbolehkan",
          (value) =>
            value &&
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/HEIC"
            )
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      setLoading(true);
      const url = `api/account/${data.id}/update`;
      // payload
      const payload = new FormData();
      payload.append("name", values.name);
      payload.append("username", values.username);
      payload.append("password", values.password);
      payload.append("email", values.email);
      payload.append("phone", values.phone);
      payload.append("adminAccount", values.adminAccount.toString());
      payload.append(
        "adminCmsLandingpage",
        values.adminCmsLandingpage.toString()
      );
      payload.append("adminBlog", values.adminBlog.toString());
      payload.append("adminCourse", values.adminCourse.toString());
      // img
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }

      cr.post(url, payload)
        .then((response) => {
          if (response.status === 200) {
            onClose();
            toast({
              title: "Update Akun Berhasil",
              description: response.data.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
            setTrigger(!trigger);
          }
        })
        .catch((error) => {
          console.log(error);
          toast({
            title: "Update Akun Gagal",
            description: error?.response?.data?.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  const handleInputRole = (role: { name: string; key: string; icon: any }) => {
    let value;
    switch (role.key) {
      case "adminAccount":
        value = !formik.values.adminAccount;
        break;
      case "adminCmsLandingpage":
        value = !formik.values.adminCmsLandingpage;
        break;
      case "adminBlog":
        value = !formik.values.adminBlog;
        break;
      case "adminCourse":
        value = !formik.values.adminCourse;
        break;
    }

    formik.setFieldValue(`${role.key}`, value);
  };

  return (
    <>
      <Button
        leftIcon={<Icon as={PencilSimpleLine} fontSize={iconSize} />}
        variant={"ghost"}
        colorScheme="ap"
        className="clicky"
        onClick={onOpen}
        // isLoading={loading}
      >
        Update
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        scrollBehavior="inside"
        size={"lg"}
      >
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>Update Akun Admin</ModalHeader>

          <ModalBody>
            <form id="updateAdminAccountForm" onSubmit={formik.handleSubmit}>
              <FormControl mb={4} isInvalid={formik.errors.name ? true : false}>
                <FormLabel>
                  Nama Lengkap
                  <RequiredForm />
                </FormLabel>
                <Input
                  name="name"
                  placeholder="Masukkan nama lengkap"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                />
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.username ? true : false}
              >
                <FormLabel>
                  Username
                  <RequiredForm />
                </FormLabel>
                <Input
                  name="username"
                  placeholder="Masukkan username"
                  onChange={formik.handleChange}
                  value={formik.values.username}
                />
                <FormHelperText opacity={0.5}>
                  Username digunakan untuk sign in akun ini
                </FormHelperText>
                <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
              </FormControl>

              <FormControl
                mb={4}
                isInvalid={formik.errors.password ? true : false}
              >
                <FormLabel>Ubah Password</FormLabel>
                <PasswordInput
                  name="password"
                  placeholder="Masukkan password baru (jika ingin diubah)"
                  formik={formik}
                  autoComplete="off"
                  readOnly={true}
                />
                <Button
                  mt={2}
                  size={"sm"}
                  colorScheme="ap"
                  variant={"ghost"}
                  className="btn-solid clicky"
                  onClick={() => {
                    const passwordInput = document.querySelector(
                      'input[name="password"]'
                    ) as HTMLInputElement;
                    if (passwordInput) {
                      passwordInput.setAttribute("aria-readonly", "false");
                      passwordInput.removeAttribute("readonly");
                      passwordInput.focus();
                    }
                  }}
                >
                  New Password
                </Button>
                <FormHelperText opacity={0.5}>
                  Klik tombol di atas agar bisa input password baru
                </FormHelperText>
                <FormErrorMessage>{formik.errors.password}</FormErrorMessage>
              </FormControl>

              {data.id !== 1 && (
                <FormControl mb={4}>
                  <FormLabel>Role</FormLabel>
                  <SimpleGrid columns={[2]} gap={2}>
                    {adminRoles.map((role, i) => (
                      <HStack
                        key={i}
                        justify={"center"}
                        borderRadius={8}
                        p={2}
                        bg={
                          // @ts-ignore
                          formik.values[role.key] ? "p.500" : "var(--divider)"
                        }
                        color={
                          // @ts-ignore
                          formik.values[role.key] ? "white" : "current"
                        }
                        cursor={"pointer"}
                        _hover={{
                          // @ts-ignore
                          bg: formik.values[role.key]
                            ? "p.500"
                            : "var(--divider2)",
                        }}
                        transition={"200ms"}
                        onClick={() => {
                          handleInputRole(role);
                        }}
                      >
                        <Icon
                          as={role.icon}
                          weight="fill"
                          fontSize={20}
                          ml={-1}
                        />
                        <Text>{role.name}</Text>
                      </HStack>
                    ))}
                  </SimpleGrid>
                </FormControl>
              )}

              <FormControl mb={4}>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  placeholder="Masukkan email"
                  onChange={formik.handleChange}
                  value={formik.values.email || ""}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Phone</FormLabel>
                <Input
                  name="phone"
                  placeholder="Masukkan no telepon"
                  onChange={formik.handleChange}
                  value={formik.values.phone || ""}
                />
              </FormControl>

              <FormControl mb={4}>
                <FormLabel>Foto Profil</FormLabel>
                <FileUploader
                  name="image"
                  formik={formik}
                  setPreviewUrl={setFotoProfilPreview}
                  accept=".jpg, .jpeg, .png, .HEIC, .svg"
                />
                {fotoProfilPreview ? (
                  <VStack p={2} bg={"var(--divider)"} mt={2} borderRadius={8}>
                    <Image
                      borderRadius={8}
                      w={"100%"}
                      src={fotoProfilPreview}
                      mt={4}
                      mx={"auto"}
                    />
                  </VStack>
                ) : (
                  <Text fontSize={"small"} opacity={0.5} mt={1}>
                    Preview akan terlihat dibawah setelah mengunggah
                  </Text>
                )}
              </FormControl>
            </form>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button className="btn clicky" onClick={onClose}>
              Batal
            </Button>

            <Button
              type="submit"
              form="updateAdminAccountForm"
              colorScheme="p"
              className="clicky"
              isLoading={loading}
            >
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
